import React, { useState, useEffect, useRef } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider  } from '@material-ui/pickers';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
    InputAdornment,
    TextField,
    Button,
    NativeSelect,
    Switch
} from '@material-ui/core';
import { getTotalPayments } from 'core/utils';
import Autocomplete, { createFilterOptions  } from '@material-ui/lab/Autocomplete';
import {
    mediateParentEntryInfoDependent,
    mediateEntriesTitleAmountInfoDependent,
    mediateTagsInfoDependent,
    mediateNotCompleteParentEntriesInfoDependent
} from 'mediators';
import { DatePicker, DateTimePicker } from '@material-ui/pickers';
import { useSelector } from 'react-redux';

const filter = createFilterOptions();
export const EntryDialog = (props) => {
    const amountRef:any | undefined = useRef();
    const [open, setOpen] = useState(false);
    const [dueDateActive, setDueDateActive] = useState(!!props.dueDate);
    const [reminderDateActive, setReminderDateActive] = useState(!!props.reminderDate);
    const [lockParent] = useState(props.lockParent);
    const entries: any | undefined = useSelector(mediateNotCompleteParentEntriesInfoDependent);
    const titleAmountViewModel: any | undefined = useSelector(mediateEntriesTitleAmountInfoDependent);
    const [name, setName] = useState(props.name || null);
    const savedParentEntry : any | undefined = useSelector(mediateParentEntryInfoDependent(props.parent));
    const [parentEntry, setParentEntry] = useState(savedParentEntry);
    const [type, setType] = useState(props.type);
    const [amount, setAmount] = useState(props.amount || '');
    const [selection, setSelection] = useState(null);
    const [tags, setTags] = useState([]);
    const tagInfoViewModel: any[] | undefined = useSelector(mediateTagsInfoDependent);
    const [selectedDate, setSelectedDate] = useState(props.dueDate ? new Date(props.dueDate) : null);
    const [selectedReminderDate, setSelectedReminderDate] = useState(props.reminderDate ? new Date(props.reminderDate) : null);

    useEffect(() => {
            if(props.tag) {
                setTags(props.tag.split(','));
            }
    }, [props.tag]);

    useEffect(() => {
        setOpen(props.open || false);
    }, [props.open]);

    useEffect(() => {
        setType(props.type);
    }, [props.type]);

    useEffect(() => {
        if(props.name && props.amount) {
            setSelection({ title : props.name, amount : props.amount });
        }
    }, [props.name, props.amount])

    const clearForm = () => {
        setSelection(null);
        setSelectedDate(null);
        setDueDateActive(false);
        setParentEntry(null);
        setName('');
        setTags([]);
        setAmount('');
    };

    const handleActivateDueDate = (evt) => {
        setDueDateActive(evt.target.checked);
    };

    const handleActivateReminderDate = (evt) => {
        setReminderDateActive(evt.target.checked);
    };

    const handleNameChange = (evt, selected, reason) => {
        if(selected && typeof selected === 'string') {
            setName(selected);
            setSelection({ title : selected, amount : '' });
            amountRef.current.focus();
        } else if(selected) {
            setName(selected.title);
            setAmount(selected.amount);
            if(selected.tag) {
                setTags(selected.tag.split(','));
            }
            amountRef.current.focus();
        } else {
            setSelection(null);
            setName('');
        }
    };

    const handleDateChange = (evt) => {
        setSelectedDate(evt);
    };

    const handleReminderDateChange = (evt) => {
        setSelectedReminderDate(evt);
    };

    const handleTagChange = (evt, selected, reason) => {
        let nextTags = selected.map((tag) => {
            let nextTag = tag;
            if(typeof tag !== 'string') {
                nextTag = nextTag.inputValue ? nextTag.inputValue : nextTag;
            }
            return nextTag;
        });
        setTags(nextTags);
    };

    const handleParentChange = (evt, selected, reason) => {
        setParentEntry(selected)
    };

    const handleAmountChange = (evt) => {
        setAmount(evt.target.value);
    };

    const handleClose = () => {
        clearForm();
        props.handleClose && props.handleClose();
    };

    const handleAction = () => {
        let tag = tags.join(',');
        clearForm();
        props.action(
            name,
            amount,
            type || 'DEBIT',
            tag,
            selectedDate && dueDateActive ? selectedDate.toISOString() : null,
            selectedReminderDate && reminderDateActive ? selectedReminderDate.toISOString() : null,
            parentEntry
        );
    };

    const handleChangeType = (evt) => {
        setType(evt.target.value);
    };
    
    const getOptionLabel = (option) => {
        return option && option.title ? option.title : option
    };

    const getTagOptionLabel = (option) => {
        return option && option.title ? option.title : option || ''
    };

    return (<Dialog transitionDuration={0} TransitionProps={{timeout : 0, enter : false, exit : false}} fullScreen open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{props.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Please enter a name and an amount
              </DialogContentText>
              <NativeSelect onChange={handleChangeType} value={type}>
                <option value='DEBIT'>Debit (-)</option>
                <option value='CREDIT'>Credit (+)</option>
              </NativeSelect>
              <Autocomplete 
                autoComplete
                autoSelect
                options={titleAmountViewModel}
                clearOnBlur
                selectOnFocus
                freeSolo
                value={selection}
                onChange={handleNameChange}
                getOptionLabel={getOptionLabel}
                fullWidth
                renderOption={(option) => {
                    if(typeof option === 'string') {
                        return option;
                    }
                    return (
                        <Box width={1} display="flex">
                            <Box flexGrow={1}>
                                <span>{option.title} </span>
                            </Box>
                            <span>{option.amount} </span>
                        </Box>
                    )
                }}
                renderInput={(params) => (
                  <TextField
                  {...params}
                    autoFocus
                    label="Name"
                  />
                )}
              />
              <TextField
                margin="dense"
                inputRef={amountRef}
                id="amount"
                label="Amount"
                type="number"
                value={amount}
                InputProps={{
                    startAdornment:<InputAdornment position="start"><Typography color={type === 'DEBIT' ? 'secondary' : 'primary'}>{type === 'DEBIT' ? '-' : '+'}$</Typography></InputAdornment>
                }}
                onChange={handleAmountChange}
                fullWidth
              />
              <Autocomplete 
                multiple
                options={tagInfoViewModel}
                filterSelectedOptions
                clearOnBlur
                getOptionLabel={getTagOptionLabel}
                freeSolo
                limitTags={3}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    // Suggest the creation of a new value
                    if (params.inputValue !== '') {
                        filtered.push({
                            inputValue: params.inputValue,
                            title : `Add "${params.inputValue}"`
                        });
                    }

                    return filtered;
                }}
                value={tags}
                onChange={handleTagChange}
                fullWidth
                renderInput={(params) => (
                  <TextField
                  {...params}
                    label="Tags"
                  />
                )}
              />
              <Autocomplete 
                options={entries}
                filterSelectedOptions
                disabled={lockParent}
                disableClearable={lockParent}
                clearOnBlur
                getOptionLabel={getTagOptionLabel}
                renderOption={(option) => {
                    if(typeof option === 'string') {
                        return option;
                    }
                    return (
                        <Box width={1} display="flex">
                            <Box flexGrow={1}>
                                <span>{option.title} </span>
                            </Box>
                            <span>{getTotalPayments(option)}/{option.amount} </span>
                        </Box>
                    )
                }}
                freeSolo
                value={parentEntry}
                onChange={handleParentChange}
                fullWidth
                renderInput={(params) => (
                  <TextField
                  {...params}
                    label="Parent item"
                  />
                )}
              />
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Box display='flex'>
                    <Box display='flex'>
                        <DatePicker disabled={!dueDateActive} clearable={true} value={selectedDate} onChange={handleDateChange} label='Due date' />
                        <Switch checked={dueDateActive} onChange={handleActivateDueDate} />
                    </Box>
                    <Box display='flex'>
                        <DateTimePicker disabled={!reminderDateActive} clearable={true} value={selectedReminderDate} onChange={handleReminderDateChange} label='Reminder' />
                        <Switch checked={reminderDateActive} onChange={handleActivateReminderDate} />
                    </Box>
                </Box>
              </MuiPickersUtilsProvider>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button disabled={!amount || !name} onClick={handleAction} color="primary">
                Save
              </Button>
            </DialogActions>
          </Dialog>)
};
